<section hlmCard>
  <div hlmCardHeader>
    <h2 hlmCardTitle>Auswahl</h2>
    <p hlmCardDescription>
      @if (isGroup()) {
        Eine Gruppe aus {{ count() }} Elementen. Ziehe sie, um sie als Ganzes zu verschieben; klicke
        noch einmal auf ein Element, um es allein zu bearbeiten, es bleibt in der Gruppe.
      } @else {
        {{ count() }} Elemente. Ziehe eines, um alle zu verschieben; die Pfeiltasten bewegen die
        Auswahl. Beim Ziehen rasten Kanten und Mitten an anderen Elementen ein; mit ⇧ nicht.
      }
    </p>
  </div>
  <div hlmCardContent class="space-y-4">
    @if (textElements().length) {
      <div class="space-y-2">
        <div class="flex items-baseline justify-between gap-2">
          <span hlmLabel>Text</span>
          @if (scopeNote(textElements().length); as note) {
            <span hlmMuted class="text-xs">{{ note }}</span>
          }
        </div>
        <button
          hlmBtn
          variant="outline"
          type="button"
          class="w-full justify-between font-normal"
          aria-label="Schriftart"
          [hlmDropdownMenuTrigger]="fontMenu"
          align="start">
          @if (commonFamily(); as family) {
            <span class="truncate text-base" [style.font-family]="cssOf(family)">
              {{ labelOf(family) }}
            </span>
          } @else {
            <span class="text-muted-foreground truncate italic">Gemischt</span>
          }
          <ng-icon name="lucideChevronDown" class="shrink-0 opacity-60" />
        </button>
        <ng-template #fontMenu>
          <hlm-dropdown-menu class="max-h-80 w-72 overflow-y-auto">
            <hlm-dropdown-menu-group>
              @for (f of fonts(); track f.family) {
                <button
                  hlmDropdownMenuItem
                  class="text-base"
                  [attr.aria-checked]="f.family === commonFamily()"
                  (click)="emit('text', { font: { family: f.family } })">
                  <ng-icon
                    name="lucideCheck"
                    class="shrink-0"
                    [class.invisible]="f.family !== commonFamily()" />
                  <span [style.font-family]="f.css">{{ f.label }}</span>
                </button>
              }
            </hlm-dropdown-menu-group>
          </hlm-dropdown-menu>
        </ng-template>
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1">
            <label hlmLabel for="sel-size">Schriftgröße (pt)</label>
            <input
              hlmInput
              id="sel-size"
              type="number"
              step="0.5"
              [min]="limits()?.font_size_min ?? 6"
              [max]="limits()?.font_size_max ?? 120"
              [value]="commonSize() ?? ''"
              [attr.placeholder]="commonSize() == null ? 'Gemischt' : null"
              (change)="onSize($event)" />
          </div>
          <div class="col-span-2 space-y-1">
            <label hlmLabel for="sel-color">Farbe</label>
            <app-color-field
              id="sel-color"
              label="Farbe"
              [value]="commonColor()"
              [placeholder]="commonColor() ? null : 'Gemischt'"
              (valueChange)="emit('text', { font: { color: $event } })" />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex gap-1" role="group" aria-label="Schriftschnitt">
            <button
              hlmBtn
              size="sm"
              type="button"
              [variant]="allBold() ? 'default' : 'outline'"
              [class.ring-1]="someBold() && !allBold()"
              [attr.aria-pressed]="allBold() ? true : someBold() ? 'mixed' : false"
              aria-label="Fett"
              [hlmTooltip]="
                someBold() && !allBold() ? 'Teils fett – Klick macht alle fett' : 'Fett'
              "
              (click)="emit('text', { font: { bold: !allBold() } })">
              <ng-icon name="lucideBold" />
            </button>
            <button
              hlmBtn
              size="sm"
              type="button"
              [variant]="allItalic() ? 'default' : 'outline'"
              [class.ring-1]="someItalic() && !allItalic()"
              [attr.aria-pressed]="allItalic() ? true : someItalic() ? 'mixed' : false"
              aria-label="Kursiv"
              [hlmTooltip]="
                someItalic() && !allItalic() ? 'Teils kursiv – Klick macht alle kursiv' : 'Kursiv'
              "
              (click)="emit('text', { font: { italic: !allItalic() } })">
              <ng-icon name="lucideItalic" />
            </button>
          </div>
          <div class="flex gap-1" role="group" aria-label="Textausrichtung">
            @for (a of aligns; track a.value) {
              <button
                hlmBtn
                size="sm"
                type="button"
                [variant]="commonAlign() === a.value ? 'default' : 'outline'"
                [attr.aria-pressed]="commonAlign() === a.value"
                [attr.aria-label]="a.label"
                [hlmTooltip]="a.label"
                (click)="emit('text', { align: a.value })">
                <ng-icon [name]="a.icon" />
              </button>
            }
          </div>
        </div>
      </div>
    }

    @if (frameElements().length) {
      <div class="space-y-2">
        <div class="flex items-baseline justify-between gap-2">
          <span hlmLabel>Rahmen</span>
          @if (scopeNote(frameElements().length); as note) {
            <span hlmMuted class="text-xs">{{ note }}</span>
          }
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1">
            <label hlmLabel for="sel-border">Rahmen (pt)</label>
            <input
              hlmInput
              id="sel-border"
              type="number"
              step="0.5"
              min="0"
              [max]="limits()?.border_width_max ?? 50"
              [value]="commonBorderWidth() ?? ''"
              [attr.placeholder]="commonBorderWidth() == null ? 'Gemischt' : null"
              (change)="onBorderWidth($event)" />
          </div>
          <div class="space-y-1">
            <label hlmLabel for="sel-radius">Ecken (pt)</label>
            <input
              hlmInput
              id="sel-radius"
              type="number"
              step="1"
              min="0"
              [value]="commonRadius() ?? ''"
              [attr.placeholder]="commonRadius() == null ? 'Gemischt' : null"
              (change)="onRadius($event)" />
          </div>
          <div class="col-span-2 space-y-1">
            <label hlmLabel for="sel-border-color">Rahmenfarbe</label>
            <app-color-field
              id="sel-border-color"
              label="Rahmenfarbe"
              [value]="commonBorderColor()"
              [placeholder]="commonBorderColor() ? null : 'Gemischt'"
              (valueChange)="emit('frame', { border_color: $event })" />
          </div>
        </div>
      </div>
    }

    @if (boxElements().length) {
      <div class="space-y-1">
        <div class="flex items-baseline justify-between gap-2">
          <label hlmLabel for="sel-fill">Füllung</label>
          @if (scopeNote(boxElements().length); as note) {
            <span hlmMuted class="text-xs">{{ note }}</span>
          }
        </div>
        <app-color-field
          id="sel-fill"
          label="Füllfarbe"
          [value]="commonFill()"
          [placeholder]="commonFill() ? null : 'Gemischt'"
          (valueChange)="emit('box', { fill: $event })" />
      </div>
    }

    @if (qrElements().length) {
      <div class="space-y-2">
        <div class="flex items-baseline justify-between gap-2">
          <span hlmLabel>QR-Code</span>
          @if (scopeNote(qrElements().length); as note) {
            <span hlmMuted class="text-xs">{{ note }}</span>
          }
        </div>
        <div class="space-y-1">
          <label hlmLabel for="sel-qr-color">Farbe des Codes</label>
          <app-color-field
            id="sel-qr-color"
            label="Farbe des Codes"
            [value]="commonQrColor()"
            [placeholder]="commonQrColor() ? null : 'Gemischt'"
            (valueChange)="emit('qr', { qr_color: $event })" />
        </div>
        <div class="space-y-1">
          <label hlmLabel for="sel-qr-background">Hintergrund</label>
          <app-color-field
            id="sel-qr-background"
            label="Hintergrundfarbe"
            [value]="commonQrBackground()"
            [placeholder]="commonQrBackground() ? null : 'Gemischt'"
            (valueChange)="emit('qr', { qr_background: $event })" />
        </div>
      </div>
    }

    @if (opacityElements().length) {
      <div class="space-y-1">
        <div class="flex items-baseline justify-between gap-2">
          <label hlmLabel for="sel-opacity">Deckkraft</label>
          @if (scopeNote(opacityElements().length); as note) {
            <span hlmMuted class="text-xs">{{ note }}</span>
          }
        </div>
        <div class="flex items-center gap-3">
          <input
            id="sel-opacity"
            type="range"
            min="10"
            max="100"
            step="5"
            class="accent-primary flex-1"
            [value]="commonOpacity() ?? 100"
            (change)="onOpacity($event)" />
          <span class="w-16 shrink-0 text-right text-sm whitespace-nowrap tabular-nums">{{
            commonOpacity() != null ? commonOpacity() + ' %' : 'Gemischt'
          }}</span>
        </div>
      </div>
    }

    <div class="border-t"></div>

    <div class="space-y-1">
      <span hlmLabel>Ausrichten</span>
      <div class="flex flex-wrap gap-1" role="group" aria-label="Ausrichten">
        @for (b of alignButtons; track b.mode) {
          <button
            hlmBtn
            variant="outline"
            size="icon-sm"
            type="button"
            [attr.aria-label]="b.label"
            [hlmTooltip]="b.label"
            (click)="align.emit(b.mode)">
            <ng-icon [name]="b.icon" />
          </button>
        }
      </div>
      <p hlmMuted class="text-xs">An der Auswahl: die Kante oder Mitte der äußersten Elemente.</p>
    </div>

    <div class="space-y-1">
      <span hlmLabel>Verteilen</span>
      <div class="flex flex-wrap gap-1" role="group" aria-label="Verteilen">
        <button
          hlmBtn
          variant="outline"
          size="sm"
          type="button"
          [disabled]="count() < 3"
          [hlmTooltip]="'Gleiche Abstände von links nach rechts'"
          (click)="distribute.emit('horizontal')">
          <ng-icon name="lucideAlignHorizontalSpaceBetween" />
          Horizontal
        </button>
        <button
          hlmBtn
          variant="outline"
          size="sm"
          type="button"
          [disabled]="count() < 3"
          [hlmTooltip]="'Gleiche Abstände von oben nach unten'"
          (click)="distribute.emit('vertical')">
          <ng-icon name="lucideAlignVerticalSpaceBetween" />
          Vertikal
        </button>
      </div>
      @if (count() < 3) {
        <p hlmMuted class="text-xs">Ab drei Elementen.</p>
      }
    </div>

    <div class="space-y-1">
      <span hlmLabel>Gruppe</span>
      <div class="flex flex-wrap gap-1">
        @if (isGroup()) {
          <button hlmBtn variant="outline" size="sm" type="button" (click)="ungroup.emit()">
            <ng-icon name="lucideUngroup" />
            Gruppierung aufheben
          </button>
        } @else {
          <button hlmBtn variant="outline" size="sm" type="button" (click)="group.emit()">
            <ng-icon name="lucideGroup" />
            Gruppieren
          </button>
          @if (hasGroups()) {
            <button hlmBtn variant="ghost" size="sm" type="button" (click)="ungroup.emit()">
              <ng-icon name="lucideUngroup" />
              Gruppen auflösen
            </button>
          }
        }
      </div>
      <p hlmMuted class="text-xs">
        Eine Gruppe wird mit einem Klick als Ganzes gewählt und verschoben. ⌘G / Strg+G gruppiert,
        ⇧⌘G löst auf.
      </p>
    </div>

    <div class="flex flex-wrap gap-2">
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        [hlmTooltip]="'Noch einmal, daneben (⌘D / Strg+D; ⌘C und ⌘V kopieren auch in andere Designs)'"
        (click)="duplicate.emit()">
        <ng-icon name="lucideCopy" />
        Duplizieren
      </button>
      <button hlmBtn variant="destructive" size="sm" type="button" (click)="remove.emit()">
        <ng-icon name="lucideTrash2" />
        Auswahl entfernen
      </button>
    </div>
  </div>
</section>
